import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from 'react-hot-toast';
import GetSvgIcon from "../assets/images/svgList";
import Loader from "../Components/GlobalLoader/Loader";
import Table, { Column } from "../Components/Tables/Table";
import { usePagination } from "../hooks/usePagination";
import { ApiResponse } from "../model/common/apiResponse";
import { FormValues } from "../Pages/ASN/model/AsnAddItem.type";
import { lookUpService } from "../Services/common/lookup.service";
import { useSearchStore } from "../store/search/searchStore";


interface OutboundOrderAddLineLayoutProps {
  data: any[];
  handleClose: () => void;
  handleAddItem: (items: any[]) => void;
  initialSearchTerm?: string;
  clientId: string;
}

const actionLinkStyle = {
  cursor: 'pointer',
  textDecoration: 'underline',
  border: 'none',
  background: 'none',
  padding: 0
};

const OutboundOrderAddLineLayout: React.FC<OutboundOrderAddLineLayoutProps> = ({
  data,
  handleClose,
  handleAddItem,
  initialSearchTerm = "",
  clientId
}) => {
  const { currentPage, handlePageChange, perPage, setCurrentPage } = usePagination();
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [rowsWithQuantity, setRowsWithQuantity] = useState<{
    [key: number]: any;
  }>({});
  const textInputRef = useRef<HTMLInputElement>(null);
  const { searchTerm, setSearchTerm, resetSearch } = useSearchStore();
  const [handleInput, setHandleInput] = useState<string>(initialSearchTerm);
  const [itemData, setItemData] = useState<ApiResponse<any> | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setHandleInput(value);
    if (value === "") {
      resetSearch();
      fetchItems();
    }
  };

  const handleSearchTerm = () => {
    if (handleInput.trim()) {
      setSearchTerm(handleInput);
      fetchItems(handleInput);
    } else {
      resetSearch();
      fetchItems();
    }
  };

  const fetchItems = async (term?: string) => {
    setCurrentPage(1);
    setSearchTerm(term??"");
    clearAllTextInputs();
  };

  const clearAllTextInputs = () => {
    const newRowsWithQuantity = { ...rowsWithQuantity };
    for (let index = 0; index < perPage; index++) {
      setValue(`items.${index}.expectedQuantity`, "", { shouldValidate: true });
      delete newRowsWithQuantity[index];
    }
    setRowsWithQuantity(newRowsWithQuantity);
  };

  useEffect(() => {
    setItemData(null);
    const loadItems = async () => {
      try {
        const response = await lookUpService.itemsLookup(searchTerm,currentPage,perPage,clientId);
        if (response.data) {
          setItemData(response.data);
          const newRowsWithQuantity = { ...rowsWithQuantity };
          response.data.data.results.forEach((row: any, index: number) => {
            setValue(`items.${index}.expectedQuantity`, "", { shouldValidate: true });
          });
          setRowsWithQuantity(newRowsWithQuantity);
        }
      } catch (error) {
        console.error("Error fetching items:", error);
      }
    };

    loadItems();
  }, [searchTerm,currentPage]);

  useEffect(() => {
    if (initialSearchTerm) {
      setHandleInput(initialSearchTerm);
      setSearchTerm(initialSearchTerm);
      fetchItems(initialSearchTerm);
    }
  }, [initialSearchTerm]);

  const {
    control,
    setValue,
    handleSubmit,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      items: [],
    },
  });

  const handleQuantityChange = (index: number, value: string, row: any) => {
    if (value && parseInt(value) > 0) {
      setSelectedRows((prev) => new Set([...Array.from(prev), index]));
      setRowsWithQuantity((prev) => ({
        ...prev,
        [index]: {
          ...row,
          ExpectedQty: value,
          id: row.id,
          code: row.code,
          description: row.description,
          uom_code: row.uom_code,
          qty_on_hand: row.qty_on_hand
        },
      }));
    } else {
      const newSelectedRows = new Set(Array.from(selectedRows));
      newSelectedRows.delete(index);
      setSelectedRows(newSelectedRows);

      const newRowsWithQuantity = { ...rowsWithQuantity };
      delete newRowsWithQuantity[index];
      setRowsWithQuantity(newRowsWithQuantity);
    }
  };

  const handleEnterPress = (event: any) => {
    if (event.key === "Enter") {
      handleSearchTerm();
    }
  };

  const canAddRow = (index: number) => {
    const quantity = rowsWithQuantity[index]?.ExpectedQty;
    return quantity && quantity !== "0" && quantity !== "";
  };

  const handleRowAdd = (index: number, row: any) => {
    if (!canAddRow(index)) return;
    
    const selectedData = [{
      ...rowsWithQuantity[parseInt(index.toString())],
      ExpectedQty: rowsWithQuantity[parseInt(index.toString())]?.ExpectedQty
    }];

    if (selectedData[0]) {
      handleAddItem(selectedData);
      toast.success('Item added successfully!', {
        duration: 3000,
      });
      setRowsWithQuantity((prev) => {
        const newRows = { ...prev };
        delete newRows[index];
        return newRows;
      });
      setValue(`items.${index}.expectedQuantity`, "", { shouldValidate: true });
    }
  };

  const columns: Column[] = [
    {
      name: "Item Code",
      cell: (row, index) => (
        <div >
          {/* <img
            alt="Item"
            src={require(`../assets/images/${row.imgSrc}`)}
            className="img-fluid thumbnail-40"
          /> */}
          <span>{row.code}</span>
        </div>
      ),
    },
    {
      name: "Item Name",
      cell: (row) => <div>{row.name}</div>,
    },
    {
      name: "UOM",
      cell: (row) => <div>{row.uom_code ?? "-"}</div>,
    },
    {
      name: "Qty on Hand",
      cell: (row) => <div>{row.qty_on_hand}</div>,
    },
    {
      name: "Ordered Qty",
      cell: (row, index) => (
        <Controller
          name={`items.${index}.expectedQuantity` as const}
          control={control}
          defaultValue={""}
          render={({ field, fieldState: { error } }) => (
            <div>
              <input
                type="text"
                {...field}
                value={field.value || ""}
                autoComplete="off"
                className="custom-asn-input text-center"
                onKeyDown={(e) => {
                  if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  field.onChange(value);
                  handleQuantityChange(index, value, row);
                  setValue(`items.${index}.expectedQuantity`, value, {
                    shouldValidate: true,
                  });
                }}
              />
              {error && <div style={{ color: "red" }}>{error.message}</div>}
            </div>
          )}
        />
      ),
    },
    {
      name: <span className="ms-4">Action</span>,
      cell: (row, index) => (
        <button
          className={`${
            !canAddRow(index) ? 'sph-btn-disabled cursor-pointer' : 'sph-btn cursor-pointer'
          }`}
          onClick={() => canAddRow(index) && handleRowAdd(index, row)}
          style={{ cursor: canAddRow(index) ? 'pointer' : 'not-allowed' }}
          disabled={!canAddRow(index)}
        >
          Add
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="count-expanded-view-list mb-3">
        <div className="input-group rounded-pill col-auto w-25">
          <input
            type="text"
            placeholder="Search"
            className="form-control rounded-start-pill border border-end-0 pe-0 search-input"
            value={handleInput}
            ref={textInputRef}
            onChange={handleInputChange}
            onKeyDown={handleEnterPress}
          />
          <span
            className="input-group-text bg-white border border-start-0 rounded-end-pill cursor-pointer"
            onClick={handleSearchTerm}
          >
            {GetSvgIcon.GlobalSearchIcon}
          </span>
        </div>
        {itemData ? (
          <Table
            columns={columns}
            data={itemData?.data?.results || []}
            enableScrollbar={true}
            onSelectedRowsChange={setSelectedRows}
            CheckboxStatus={false}
            customClassName="add-line-items" 
            customDivClassName="add-line-items-container"
            pagination={{
              recordsPerPage: perPage,
              totalRecords: itemData?.data?.recordsTotal,
              currentPage: currentPage,
              onCurrentPageChange: handlePageChange,
            }}

          />
        ) : (
          <Loader />
        )}
      </div>
    </div>
  );
};

export default OutboundOrderAddLineLayout;
